import {SaveClearJobStop, SearchStopFilter} from "../../generated/graphql";
import {AdjustableStop} from "../../components/common/types/enhanced";

/** Minutes-adjustment payload produced by the Adjust Stop Time modal. */
export type AdjustTimeState = Pick<SaveClearJobStop, "adjustScheduledMinutes" | "adjustLateMinutes">;

/**
 * searchStops filter selecting every stop belonging to any of the given jobs.
 * Uses an OR of `eq` because ModelIntFilterInput has no `in` operator (DP-3051 D9).
 */
export const buildStopsByJobsFilter = (jobIds: number[]): SearchStopFilter => ({
  stop: {or: jobIds.map((jobId) => ({job_jobId: {eq: jobId}}))}
});

/** Distinct job ids across the given stops (stops without a job are ignored). */
export const getDistinctJobIds = (stops: AdjustableStop[]): number[] => [
  ...new Set(stops.map((stop) => stop.job?.jobId).filter((id): id is number => id != null))
];

/** How many distinct jobs the selection spans (drives single vs mass wording/rules). */
export const getDistinctJobCount = (stops: AdjustableStop[]): number => getDistinctJobIds(stops).length;

/**
 * The stops a context-menu action should target: the multi-selection when the user
 * selected more than one row, otherwise just the right-clicked row.
 */
export const resolveTargetStops = <T extends AdjustableStop>(selectedStops: T[], clickedStop?: T | null): T[] => {
  if (selectedStops.length > 1) {
    return selectedStops;
  }
  if (clickedStop) {
    return [clickedStop];
  }
  return [];
};

/**
 * For each selected job, the stop with the smallest jobStopId. The backend cascades a
 * job's stop-time adjustment from this representative stop to the job's remaining stops,
 * so only this one stop per job needs to be submitted (DP-3051).
 */
export const getRepresentativeStopPerJob = (stops: AdjustableStop[]): AdjustableStop[] => {
  const byJob = new Map<number, AdjustableStop>();
  stops.forEach((stop) => {
    const jobId = stop.job?.jobId;
    if (jobId == null) {
      return;
    }
    const current = byJob.get(jobId);
    if (!current || stop.jobStopId < current.jobStopId) {
      byJob.set(jobId, stop);
    }
  });
  return [...byJob.values()];
};

/**
 * One saveClearJobStops payload per selected job. Each payload carries ONLY that job's
 * representative stop (smallest jobStopId) — the backend applies the adjustment to the
 * job's other stops. Callers fire one mutation per payload (DP-3051).
 */
export const buildAdjustStopCalls = (stops: AdjustableStop[], state: AdjustTimeState): SaveClearJobStop[][] =>
  getRepresentativeStopPerJob(stops).map((stop) => [{jobStopId: stop.jobStopId, ...state}]);

/**
 * Job-count-aware toast wording. Single-job wording is preserved verbatim from the
 * original Manifest Details flow; mass mode reports the job count.
 */
export const buildAdjustToastMessages = (stops: AdjustableStop[]): {success: string; failed: string} => {
  const jobIds = getDistinctJobIds(stops);
  if (jobIds.length > 1) {
    return {
      success: `The adjusted stop times for ${jobIds.length} jobs have been submitted. This adjustment will show on your screen once it’s updated in the platform for all users.`,
      failed: `Unable to adjust Stop times for ${jobIds.length} jobs`
    };
  }
  const jobNumber = stops.at(0)?.job?.jobNumber;
  return {
    success: `The adjusted stop times for job #${jobNumber} have been submitted. This adjustment will show on your screen once it’s updated in the platform for all users.`,
    failed: `Unable to adjust Stop times for Job #${jobNumber}`
  };
};
